import type { Vec3 } from '../domain';
import type { IViewport } from '../viewports';
import type { IInteractionPoints } from './types';

/**
 * Compute an IInteractionPoints from raw coordinates and a viewport.
 *
 * Every position is expressed in page, client, canvas and world coordinate spaces.
 * World coordinates are always (re)computed from the canvas position using the
 * viewport's current camera, so they stay correct even when the camera has
 * changed since the last event.
 */
export function computeEventPoints(
    clientX: number,
    clientY: number,
    pageX: number,
    pageY: number,
    viewport: IViewport
): IInteractionPoints {
    const canvas = viewport.getCanvas();

    if (!canvas) {
        throw new Error(`Can't get canvas for viewport ${viewport.id}`);
    }

    const rect = canvas.getBoundingClientRect();
    const canvasX = clientX - rect.left;
    const canvasY = clientY - rect.top;

    const world: Vec3 = viewport.canvasToWorld(canvasX, canvasY);

    return {
        page: [pageX, pageY],
        client: [clientX, clientY],
        canvas: [canvasX, canvasY],
        world,
    };
}

/**
 * Compute delta between two IInteractionPoints (current - last) in all spaces.
 */
export function computeDeltaPoints(
    current: IInteractionPoints,
    last: IInteractionPoints
): IInteractionPoints {
    return {
        page: [current.page[0] - last.page[0], current.page[1] - last.page[1]],
        client: [current.client[0] - last.client[0], current.client[1] - last.client[1]],
        canvas: [current.canvas[0] - last.canvas[0], current.canvas[1] - last.canvas[1]],
        world: [
            current.world[0] - last.world[0],
            current.world[1] - last.world[1],
            current.world[2] - last.world[2],
        ],
    };
}
